import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Tag,
  Icon,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Collapse,
  Button,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useState } from 'react';
import { CaseStudy } from '../../data/caseStudies';

const MotionBox = motion(Box);

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
  index: number;
}

export const CaseStudyCard = ({ caseStudy, index }: CaseStudyCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const industryColor = useColorModeValue('brand.500', 'brand.300');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Box
        bg={cardBg}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        overflow="hidden"
        _hover={{
          shadow: 'xl',
          borderColor: 'brand.500',
        }}
        transition="all 0.3s ease"
      >
        <Box p={6}>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between" align="start">
              <HStack spacing={3}>
                <Icon
                  as={caseStudy.icon}
                  w={8}
                  h={8}
                  color={industryColor}
                />
                <Box>
                  <Heading as="h3" size="md">
                    {caseStudy.title}
                  </Heading>
                  <Text fontSize="sm" color={mutedText}>
                    {caseStudy.industry}
                  </Text>
                </Box>
              </HStack>
            </HStack>

            <Text color={mutedText} noOfLines={isExpanded ? undefined : 2}>
              {caseStudy.challenge}
            </Text>

            <Collapse in={isExpanded} animateOpacity>
              <VStack align="stretch" spacing={4} pt={2}>
                <Box>
                  <Heading as="h4" size="sm" mb={2}>
                    Solution
                  </Heading>
                  <Text color={mutedText}>{caseStudy.solution}</Text>
                </Box>

                <Box>
                  <Heading as="h4" size="sm" mb={2}>
                    Results
                  </Heading>
                  <List spacing={2}>
                    {caseStudy.results.map((result, i) => (
                      <ListItem key={i} color={mutedText}>
                        <ListIcon as={FaCheckCircle} color="green.500" />
                        {result}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </VStack>
            </Collapse>

            <HStack flexWrap="wrap" gap={2}>
              {caseStudy.technologies.slice(0, isExpanded ? undefined : 3).map((tech) => (
                <Tag
                  key={tech}
                  size="sm"
                  colorScheme="brand"
                  variant="subtle"
                >
                  {tech}
                </Tag>
              ))}
              {!isExpanded && caseStudy.technologies.length > 3 && (
                <Tag size="sm" variant="outline">
                  +{caseStudy.technologies.length - 3}
                </Tag>
              )}
            </HStack>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              rightIcon={isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              alignSelf="flex-start"
            >
              {isExpanded ? 'Show Less' : 'Read More'}
            </Button>
          </VStack>
        </Box>
      </Box>
    </MotionBox>
  );
};
